// TESSERAX — HARMONIQUE : le puits comme INSTRUMENT (cœur pur, testé).
//
// La musique générative (main.ts) réagit déjà à l'intensité ; on la promeut de simple RETOUR à une
// RÉCOMPENSE ressentie. Quand un effacement tombe SUR LE TEMPS (proche d'une subdivision de la mesure)
// ou dessine une figure dense, on déclenche une note consonante dans la tonalité du monde courant
// (cf. core/harmony.PENT/ROOT_SETS) + une fioriture visuelle : au fil d'une run, les effacements
// COMPOSENT. Sur la durée, les rejeux deviennent des pièces audiovisuelles partageables.
//
// GARDE-FOU DÉTERMINISME (décisif) : un bonus de SCORE calé sur la phase audio (horloge mur) ferait
// diverger deux joueurs sur la même graine du jour → rejeu/verify cassés. La Harmonique v1 est donc
// STRICTEMENT NEUTRE AU SCORE : récompense sonore/visuelle uniquement (aucun RNG, aucun point). Le
// module ne fait que la CARTOGRAPHIE (phase → sur-le-temps, figure → degré de gamme) ; main.ts fournit
// la phase depuis son horloge musicale et joue la note. Pur : ni THREE ni DOM ni Math.random.
import { PENT } from './harmony';

// Force « sur le temps » d'une phase de mesure (0..1, 0 = temps fort), pour `beats` subdivisions
// (défaut 4 = noires). 1 pile sur une subdivision, décroît linéairement jusqu'à 0 à mi-chemin entre
// deux subdivisions. Tolérant aux entrées non finies (→ 0). Phase repliée dans [0,1).
export function beatStrength(phase: number, beats = 4): number {
  if (!Number.isFinite(phase) || beats <= 0) return 0;
  const p = ((phase % 1) + 1) % 1;
  const sub = 1 / beats;
  const nearest = Math.round(p / sub) * sub;
  const dist = Math.abs(p - nearest);
  return Math.max(0, 1 - dist / (sub / 2));
}

// Degré de gamme (index dans PENT) tiré de la MAGNITUDE de l'effacement : plus un clear est dense /
// 4D, plus la note monte → la run dessine un contour ASCENDANT quand le jeu s'intensifie. Borné à PENT.
export function harmoniqueDegree(rows: number, slabs: number, tesseras = 0): number {
  const mag = Math.max(0, Math.floor(rows || 0)) + Math.max(0, Math.floor(slabs || 0)) * 3 + Math.max(0, Math.floor(tesseras || 0)) * 2;
  return Math.max(0, Math.min(PENT.length - 1, mag - 1));
}

export interface HarmoniqueInput { phase: number; rows: number; slabs: number; tesseras?: number; beats?: number; }
export interface Harmonique { onBeat: boolean; strength: number; degree: number; semitone: number; }

// Seuil « sur le temps » : au-delà, on récompense (note + fioriture). 0.55 ≈ ~quart de subdivision.
export const ONBEAT_THRESHOLD = 0.55;

// Cartographie d'un effacement → récompense Harmonique (NEUTRE AU SCORE). `semitone` = demi-ton relatif
// à la racine du monde (à multiplier en Hz côté main.ts via 2^(semitone/12)).
export function harmonique(inp: HarmoniqueInput): Harmonique {
  const strength = beatStrength(inp.phase, inp.beats || 4);
  const degree = harmoniqueDegree(inp.rows, inp.slabs, inp.tesseras || 0);
  return { onBeat: strength >= ONBEAT_THRESHOLD, strength, degree, semitone: PENT[degree] };
}
